import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { Button } from 'chaoskit/src/components';

import { Data } from '../../components';
import GradientList from '../global/GradientList';
import virtualMachines from '../../assets/media/product/test/virtual-machines.svg';
import containers from '../../assets/media/product/test/containers.svg';
import kubernetes from '../../assets/media/product/test/kubernetes.svg';
import serverless from '../../assets/media/product/test/serverless.svg';
import bareMetal from '../../assets/media/product/test/bare-metal.svg';
import slack from '../../assets/media/logo-slack.svg';
import datadog from '../../assets/media/logo-datadog.svg';
import spinnaker from '../../assets/media/logo-spinnaker.svg';

import production from '../../assets/media/product/test/production.svg';

import leastPermissions from '../../assets/media/product/test/least-permissions.svg';
import readyForProduction from '../../assets/media/product/test/ready-production.svg';
import auditTrails from '../../assets/media/product/test/audit-trails.svg';
import partyTesting from '../../assets/media/product/test/3rd-party-testing.svg';
import terminal from '../../assets/media/product/test/terminal.svg';

const SecureGridItem = ({ item }) => {
  let src;

  // eslint-disable-next-line default-case
  switch (item.icon) {
    case 'leastPermissions':
      src = leastPermissions;
      break;
    case 'readyForProduction':
      src = readyForProduction;
      break;
    case 'auditTrails':
      src = auditTrails;
      break;
    case 'partyTesting':
      src = partyTesting;
      break;
  }

  return (
    <div className="product__secureItemGrid">
      <img src={src} alt="" />
      <div className="product__secureItemGrid-content">
        <h5 className="u-textLarge u-mb--small">{item.heading}</h5>
        <div className="u-textSmall">
          <Data markdown>{item.blurb}</Data>
        </div>
      </div>
    </div>
  );
};

SecureGridItem.propTypes = {
  item: PropTypes.object.isRequired,
};

const Integration = ({ service }) => {
  let src;
  let width;

  // eslint-disable-next-line default-case
  switch (service) {
    case 'Slack':
      src = slack;
      width = '160px';
      break;
    case 'Datadog':
      src = datadog;
      width = '80px';
      break;
    case 'Spinnaker':
      src = spinnaker;
      width = '80px';
      break;
  }

  return (
    <div className="product__integration-wrapper">
      <img src={src} style={{ width }} alt="" />
    </div>
  );
};

Integration.propTypes = {
  service: PropTypes.string.isRequired,
};

const Tool = ({ item: { service } }) => {
  let src;

  // eslint-disable-next-line default-case
  switch (service) {
    case 'Virtual Machines':
      src = virtualMachines;
      break;
    case 'Containers':
      src = containers;
      break;
    case 'Kubernetes':
      src = kubernetes;
      break;
    case 'Serverless':
      src = serverless;
      break;
    case 'Bare Metal':
      src = bareMetal;
      break;
  }

  return (
    <div className="product__toolsGrid">
      <img src={src} alt="" />
      <h5 className="product__toolsGrid-header u-textFluid--large">
        {service.split(' ').map(chunk => (
          <span key={chunk}>{chunk}</span>
        ))}
      </h5>
    </div>
  );
};

Tool.propTypes = {
  item: PropTypes.object.isRequired,
};

const ProductTest = () => {
  const {
    file: {
      childDataYaml: { test: pageData },
    },
  } = useStaticQuery(graphql`
    query ProductTestPageData {
      file(name: { eq: "product" }) {
        childDataYaml {
          test {
            tools {
              heading
              blurb
              items {
                service
              }
              integrations {
                service
              }
            }
            production {
              heading
              blurb
              items {
                blurb
              }
            }
            secure {
              heading
              blurb
              items {
                heading
                blurb
                icon
              }
              cta {
                label
                url
              }
            }
            simple {
              heading
              blurb
            }
          }
        }
      }
    }
  `);

  return (
    <Fragment>
      <section className="section section--large">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.tools.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.tools.blurb}</Data>
          </div>
        </div>
        <div className="product__toolsCombo">
          <div className="shared__overflowScroll">
            {pageData.tools.items.map(item => (
              <Tool key={item.service} item={item} />
            ))}
          </div>
          <div className="u-mt--xlarge">
            <div className="shared__overflowScroll u-flexMiddle">
              {pageData.tools.integrations.map(integration => (
                <Integration
                  key={integration.service}
                  service={integration.service}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.production.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.production.blurb}</Data>
          </div>
        </div>
        <div className="product__gridBlock product__gridBlock--large">
          <img
            src={production}
            className="product__gridBlock--production"
            alt=""
          />
          <div className="product__gridBlock-content">
            <GradientList items={pageData.production.items} />
          </div>
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.secure.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.secure.blurb}</Data>
          </div>
        </div>
        <div className="product__secureGrid">
          {pageData.secure.items.map(item => (
            <SecureGridItem key={item.heading} item={item} />
          ))}
        </div>
        <div className="u-textCenter">
          <Button type="primary" url={pageData.secure.cta.url}>
            {pageData.secure.cta.label}
          </Button>
        </div>
      </section>
      <section className="section section--large product__section u-pb--remove">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.simple.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.simple.blurb}</Data>
          </div>
        </div>
        <div className="product__terminal">
          <div className="product__terminal-code">
            <span>
              echo "deb https://deb.gremlin.com/release non-free" | sudo tee
              /etc/apt/sources.list.d/gremlin.list
            </span>
            <span>
              sudo apt-key adv --keyserver key server.ubuntu.com--recv-keys
              C81FC2F43A48B25808F9583DBFF170F324D41134
              9CDB294B29A5B1E2E00C24C022E8EF3461A50EF6
            </span>
            <span>
              sudo apt-get update && sudo apt-get install -y gremlin gremlin
            </span>
          </div>
          <img src={terminal} alt="" />
        </div>
      </section>
    </Fragment>
  );
};

export default ProductTest;
